import { state, type Screen } from "../state";
import type { ScreenContext } from "../app";
import { refreshHeaderCredits, renderHeader } from "../components";
import { triggerSave } from "../persistence";
import { navIconsHtml, wireNavIcons } from "../navIcons";
import { lotClickOpensPopup } from "../paneLayout";
import { formatEndurance, fuelHeadline, provisionsSummary } from "../../endurance";
import { undockWarning } from "../../undockCheck";
import { beginScreenTransition, endScreenTransition } from "../transitionGate";
import { MAX_WEAPON_LEVEL, WEAPON_KEYS, WEAPON_UPGRADE_COST_CR, upgradeWeapon, weaponLevel, type WeaponKey } from "../../weaponUpgrades";
import { PLANETARY_DEFENSE_RANGE_M } from "../../planetaryDefenses";
import { BASE_LOT_BUILD_COST_CR, buildLot, findBaseAt, isLotBuilt, type BaseRecord } from "../../baseStorage";
import { amnestyCostCr, applyAmnesty, standingTier } from "../../factionStanding";
import { stationGuardFaction, type GalaxyRegion } from "../../stationGuards";
import { getSystem } from "../../data/loadSystems";
import {
  HP_PER_FERRITE,
  REPAIR_ORE,
  STATION_REPAIR_FEE_FRACTION,
  canAffordRepair,
  damageFor,
  isDamaged,
  repairCostLabel,
  repairQuote,
  type ShipDamage
} from "../../shipDamage";
import type { PlayerShip } from "../../types";

// Base View & Station View (design doc Section 0/6/12, extended per Don's
// direction): "predetermined lots, not free placement... a fixed number
// of buildable lot positions baked into that specific image." No PNG
// layout exists yet, so per Don's explicit instruction this pass just
// sorts and aligns evenly-sized rounded boxes alphabetically on a flat
// gradient background -- a real placeholder, not the locked art-based lot
// system, which the doc itself allows falling back to (Section 6: "if a
// themed image isn't found... fall back to the plain CSS/HTML placeholder
// visual"). 16.102 changes the interaction: HOVERING a lot shows its detail
// and CLICKING it opens the module itself, full screen. The popup survives only
// for modules whose UI genuinely IS the popup, and for an unbuilt lot.
//
// Real vs. stub per module: Shipyard/Captain's Lounge/Fleet Roster/
// Market/Mission Board all open their own already-real screens, reached
// directly by clicking the lot since 16.102 -- there is no intermediate
// summary popup for them any more. Crew Quarters is real and display-only (Section 12:
// "no hiring or firing happens here"), showing the real aggregate crew
// count across the fleet. Launchpad/Dock are real navigation into the
// Expedition View, with a brief launch/undock animation. Cargo Warehouse
// and Resource Stockpile (Base only) both open the real Cargo UI
// (cargoScreen.ts), which shows this Base's real storage
// (src/baseStorage.ts) once docked -- the two lot names are the doc's own
// separate module labels, but there's one real storage system underneath
// both, not two. Armory (Base + Station) is real too (16.36) -- not a
// weapon-module inventory/fitting system (16.16 keeps the fixed roster
// locked), just per-weapon upgrade levels (1-3) on state.currentShip's
// three real combat Hardpoints (src/weaponUpgrades.ts).
//
// Base ownership is now real (16.35): a planet Location's Buy Lease/Dock
// actions in the Expedition View are the only way here in normal play --
// gated on state.ownedLeases, not a free nav destination. This screen
// itself doesn't re-check that gate (DEV DEBUG's nav bar can still jump
// here directly for testing, matching every other screen it lists).
//
// Lot construction (16.42): every Base starts with all lots EMPTY
// (Section 12: "predetermined lots... every lot is tappable and shows
// the module it's earmarked for" before anything's built) -- a fresh
// lease is real land, not a pre-furnished base. Only Base lots gate on
// this; Station modules are faction-owned infrastructure the player never
// builds (Section 12: "Stations always have a Shipyard built in"), so
// STATION_MODULES stay exactly as real/immediate as they always were.
// Launchpad is exempt even at a Base -- it's not a purchasable lot, it's
// what makes leased land a Base at all, so there's always a way to leave.

export type LotVariant = "base" | "station";

// Matches expeditionScreen.ts's own WEAPON_SHELLS labels exactly (not
// imported -- that file doesn't export its module-shell list, and
// duplicating 3 display strings is cheaper than exporting a whole
// internal type for it).
const WEAPON_LABELS: Record<WeaponKey, string> = {
  laser: "Laser Turret",
  auto: "Auto-Turret",
  missile: "Missile Turret"
};

interface LotModule {
  name: string;
  description: string;
  kind: "navigate" | "crewQuarters" | "comingSoon" | "launch" | "armory" | "planetaryDefenses" | "repair" | "amnesty";
  navigateTo?: Screen;
  navigateLabel?: string;
}

// 16.81 adds the two paid repair venues below: a Station's Drydock is a
// faction yard and charges a real service fee on top of materials; a
// Base's Repair Bay charges no fee at all, only the Ferrite -- you already
// bought the land and built the bay.

const BASE_MODULES: LotModule[] = [
  { name: "Armory", description: "Upgrade your ship's weapon levels.", kind: "armory" },
  { name: "Cargo Warehouse", description: "Transfer valuable loot and trade goods between your ship and this Base's storage.", kind: "navigate", navigateTo: "cargo", navigateLabel: "Open Cargo" },
  { name: "Captain's Lounge", description: "Review your own record -- skills, missions run, kills, and losses.", kind: "navigate", navigateTo: "lounge", navigateLabel: "Open Captain's Lounge" },
  { name: "Crew Quarters", description: "Crew statistics and where they're currently assigned.", kind: "crewQuarters" },
  { name: "Fleet Roster", description: "Manage your already-assembled fleet.", kind: "navigate", navigateTo: "fleet", navigateLabel: "Open Fleet Roster" },
  { name: "Launchpad", description: "Launch into the Expedition View.", kind: "launch" },
  { name: "Market", description: "Buy and sell cargo -- resources, loot, goods, and provisions.", kind: "navigate", navigateTo: "market", navigateLabel: "Open Market" },
  { name: "Mission Board", description: "Browse and accept missions.", kind: "navigate", navigateTo: "board", navigateLabel: "Open Mission Board" },
  { name: "Planetary Defenses", description: "Automated batteries that destroy any hostile approaching this planet.", kind: "planetaryDefenses" },
  { name: "Repair Bay", description: "Repair hull and shields using your own materials -- your crew, your yard, no service fee.", kind: "repair" },
  { name: "Resource Stockpile", description: "Transfer mined resources between your ship and this Base's storage.", kind: "navigate", navigateTo: "cargo", navigateLabel: "Open Cargo" },
  { name: "Shipyard", description: "Buy or hire hulls for your fleet.", kind: "navigate", navigateTo: "shipyard", navigateLabel: "Open Shipyard" }
];

const STATION_MODULES: LotModule[] = [
  { name: "Armory", description: "Upgrade your ship's weapon levels.", kind: "armory" },
  // 16.95: the only route out of Hostile standing. There is no decay anywhere
  // in the standing system (Don's ruling), so a captain who has burned a
  // faction either pays this or never docks in their space again.
  { name: "Registry Office", description: "Settle outstanding grievances with this station's faction -- for a price.", kind: "amnesty" },
  { name: "Captain's Lounge", description: "Review your own record -- skills, missions run, kills, and losses.", kind: "navigate", navigateTo: "lounge", navigateLabel: "Open Captain's Lounge" },
  { name: "Crew Quarters", description: "Crew statistics and where they're currently assigned.", kind: "crewQuarters" },
  { name: "Dock", description: "Undock into the Expedition View.", kind: "launch" },
  { name: "Fleet Roster", description: "Manage your already-assembled fleet.", kind: "navigate", navigateTo: "fleet", navigateLabel: "Open Fleet Roster" },
  { name: "Market", description: "Buy and sell cargo -- resources, loot, goods, and provisions.", kind: "navigate", navigateTo: "market", navigateLabel: "Open Market" },
  { name: "Mission Board", description: "Browse and accept missions.", kind: "navigate", navigateTo: "board", navigateLabel: "Open Mission Board" },
  { name: "Drydock", description: "Faction repair yard -- hull and shields restored for a service fee plus materials.", kind: "repair" },
  { name: "Shipyard", description: "Buy or hire hulls for your fleet.", kind: "navigate", navigateTo: "shipyard", navigateLabel: "Open Shipyard" }
];

function fleetCrewSummary(): { total: number; rows: { name: string; crew: number }[] } {
  const rows: { name: string; crew: number }[] = [];
  if (state.currentShip) {
    rows.push({ name: `${state.currentShip.name} (flying)`, crew: state.currentShip.crew });
  }
  state.fleet.forEach((f) => {
    if (state.currentShip && f.ship.name === state.currentShip.name && f.ship.variant === state.currentShip.variant) return;
    rows.push({ name: `${f.ship.name} (${f.source})`, crew: f.ship.crew });
  });
  const total = rows.reduce((sum, r) => sum + r.crew, 0);
  return { total, rows };
}

export function renderLotViewScreen(container: HTMLElement, ctx: ScreenContext, variant: LotVariant): void {
  state.lastTopLevelView = variant;
  const modules = [...(variant === "base" ? BASE_MODULES : STATION_MODULES)].sort((a, b) => a.name.localeCompare(b.name));
  let openModule: LotModule | null = null;
  // Set when the pre-undock check has something to say and the captain has not
  // yet answered it. Screen-local, not saved: it is a question, not a state.
  let pendingUndock = false;

  // Only a Base's lots are ever unbuilt -- a Station never needs this.
  const base: BaseRecord | undefined =
    variant === "base" ? findBaseAt(state.bases, state.currentSystemId, state.currentLocationId) : undefined;

  function builtAt(m: LotModule): boolean {
    // Launchpad and every Station module are never gated -- only Base
    // modules with a real BASE_LOT_BUILD_COST_CR entry can be unbuilt.
    if (!base || !(m.name in BASE_LOT_BUILD_COST_CR)) return true;
    return isLotBuilt(base, m.name);
  }

  renderHeader(container, ctx);

  const wrap = document.createElement("div");
  wrap.className = "lot-view";
  container.appendChild(wrap);

  function render(): void {
    wrap.innerHTML = `
      ${navIconsHtml(variant === "base" ? "base" : "station", true, { inEscapePod: state.inEscapePod, isLoanerShip: state.isLoanerShip })}
      <h1>${variant === "base" ? "Base View" : "Station View"}</h1>
      <p class="dim">
        ${variant === "base" ? "Your owned base." : "Docked at a faction Station."}
        Lot art isn't final yet -- modules are placeholder boxes, alphabetically sorted, per Don's direction.
      </p>
      <div class="lot-grid">
        ${modules.map((m) => lotBoxHtml(m)).join("")}
      </div>
      ${pendingUndock ? undockWarningHtml(currentUndockWarning()) : openModule ? modalHtml(openModule) : ""}
    `;
    refreshHeaderCredits(); // Repair/Armory/Build all spend without navigating away

    // The same row the Expedition View carries (16.98). A docked captain used
    // to have no Options, Handbook, Galaxy Map or Save without undocking first.
    wireNavIcons(
      wrap,
      (screen) => {
        triggerSave(ctx);
        ctx.navigate(screen);
      },
      () => {
        triggerSave(ctx);
        ctx.navigate("mainMenu");
      }
    );

    wrap.querySelectorAll<HTMLElement>(".lot-box").forEach((box) => {
      box.addEventListener("click", () => {
        const m = modules.find((x) => x.name === box.dataset.module) ?? null;
        if (!m) return;
        // 16.102: a built module that owns a real screen opens it directly. The
        // popup survives only for modules whose UI genuinely IS the popup
        // (Armory, Drydock/Repair Bay, Registry Office, Crew Quarters,
        // Planetary Defenses, Launch) and for an unbuilt lot, which needs its
        // Build prompt before there is anything to open.
        if (!lotClickOpensPopup(m.kind, builtAt(m)) && m.navigateTo) {
          triggerSave(ctx);
          ctx.navigate(m.navigateTo);
          return;
        }
        openModule = m;
        render();
      });
    });

    const modal = wrap.querySelector<HTMLElement>("[data-modal]");
    modal?.addEventListener("click", (ev) => {
      if (ev.target === modal) {
        openModule = null;
        render();
      }
    });
    wrap.querySelector<HTMLElement>("[data-modal-close]")?.addEventListener("click", () => {
      openModule = null;
      render();
    });
    wrap.querySelector<HTMLButtonElement>("[data-modal-navigate]")?.addEventListener("click", () => {
      const to = wrap.querySelector<HTMLButtonElement>("[data-modal-navigate]")!.dataset.modalNavigate as Screen;
      ctx.navigate(to);
    });
    wrap.querySelector<HTMLButtonElement>("[data-modal-launch]")?.addEventListener("click", () => {
      // 16.104: check before committing. The dock is the only place either pool
      // can be refilled, which is why the check belongs here and not in flight.
      if (currentUndockWarning().needed) {
        openModule = null;
        pendingUndock = true;
        render();
        return;
      }
      launchNow();
    });
    wrap.querySelector<HTMLButtonElement>("[data-undock-anyway]")?.addEventListener("click", () => {
      pendingUndock = false;
      launchNow();
    });
    wrap.querySelector<HTMLButtonElement>("[data-undock-cancel]")?.addEventListener("click", () => {
      pendingUndock = false;
      render();
    });
    wrap.querySelector<HTMLButtonElement>("[data-undock-restock]")?.addEventListener("click", () => {
      // At a Station the supplies are on the Market; at a Base they are in your
      // own stockpile, reached through the Cargo transfer screen (Don named both).
      pendingUndock = false;
      triggerSave(ctx);
      ctx.navigate(variant === "base" ? "cargo" : "market");
    });

    wrap.querySelectorAll<HTMLButtonElement>("[data-upgrade-weapon]").forEach((btn) => {
      btn.addEventListener("click", () => {
        upgradeCurrentShipWeapon(btn.dataset.upgradeWeapon as WeaponKey);
        render();
      });
    });
    wrap.querySelectorAll<HTMLButtonElement>("[data-repair-ship]").forEach((btn) => {
      btn.addEventListener("click", () => {
        repairShip(btn.dataset.repairShip!);
        render();
      });
    });
    wrap.querySelector<HTMLButtonElement>("[data-amnesty]")?.addEventListener("click", () => {
      const system = getSystem(state.currentSystemId);
      const faction = stationGuardFaction((system?.region ?? "neutral") as GalaxyRegion);
      if (!faction) return;
      const cost = amnestyCostCr(state.standing[faction]);
      if (cost <= 0 || state.credits < cost) return;
      state.credits -= cost;
      state.standing = applyAmnesty(state.standing, faction);
      state.lastEvent = `Paid ${cost.toLocaleString()} CR to settle your record with the ${faction}.`;
      triggerSave(ctx);
      ctx.rerender();
    });
    wrap.querySelector<HTMLButtonElement>("[data-build-lot]")?.addEventListener("click", () => {
      buildOpenLot();
      render();
    });
  }

  // Build (16.42): credits-only, matching the same gap ship purchases
  // already have -- PlayerShip.costResources is displayed but never
  // charged either (shipCardScreen.ts/shipyardScreen.ts), so this isn't a
  // new inconsistency. A no-op if there's no owned Base here, the module
  // has no cost entry, or credits are short (the button is disabled in
  // that case anyway, this is the defensive real check).
  function buildOpenLot(): void {
    if (!base || !openModule) return;
    const cost = BASE_LOT_BUILD_COST_CR[openModule.name];
    if (cost === undefined || state.credits < cost) return;
    state.credits -= cost;
    buildLot(base, openModule.name);
    state.lastEvent = `Built ${openModule.name} for ${cost.toLocaleString()} CR.`;
    triggerSave(ctx);
  }

  // Armory (Don's direction, 16.36): "spend a little credits to increase
  // each of the three weapons from lvl 1 to 2 to 3" -- operates on
  // state.currentShip only (the ship you're physically at, docked here),
  // not the whole fleet. A no-op if there's no ship, no instance id, or
  // the weapon's already at MAX_WEAPON_LEVEL (the button is disabled in
  // that case anyway, this is the defensive real check).
  function upgradeCurrentShipWeapon(weapon: WeaponKey): void {
    if (!state.currentShip || !state.currentShipInstanceId) return;
    const result = upgradeWeapon(state.weaponUpgrades, state.currentShipInstanceId, weapon);
    if (!result || state.credits < result.cost) return;
    state.credits -= result.cost;
    state.weaponUpgrades = result.upgrades;
    state.lastEvent = `Upgraded ${weapon} to level ${weaponLevel(result.upgrades, state.currentShipInstanceId, weapon)}.`;
    triggerSave(ctx);
  }

  /**
   * A lot, plus the info that used to require a click (16.102, Don's direction).
   *
   * The old flow was two steps for one intention: click a lot, read a popup that
   * mostly restated the box, then press its Open button. Now the info arrives on
   * HOVER and the click goes straight to the module.
   *
   * The description stays inline as well as in the hover panel, deliberately.
   * This is a tablet-first game (16.83) and touch devices have no hover, so a
   * hover-only detail would simply be unreachable there. The hover panel is what
   * carries the extra status line -- and it is also where the text will live once
   * real lot art replaces these boxes and there is no room for a paragraph.
   */
  function lotBoxHtml(m: LotModule): string {
    const built = builtAt(m);
    const status = lotStatusLine(m);
    return `
      <div class="lot-box ${built ? "" : "lot-box-unbuilt"}" data-module="${m.name}">
        <div class="lot-box-name">${m.name}${built ? "" : ' <span class="pill">unbuilt</span>'}</div>
        <div class="lot-box-desc">${m.description}</div>
        ${status ? `<div class="lot-box-status">${status}</div>` : ""}
        <div class="lot-box-info" role="tooltip">
          <strong>${m.name}</strong>
          <span class="dim">${m.description}</span>
          ${status ? `<span class="lot-box-info-status">${status}</span>` : ""}
          <span class="lot-box-info-hint">${built ? lotOpenHint(m) : "Click to see the build cost"}</span>
        </div>
      </div>
    `;
  }

  /**
   * The one line the popup used to be worth opening for -- what this lot will
   * cost or do right now, rather than what it is in general.
   */
  function lotStatusLine(m: LotModule): string {
    if (!builtAt(m)) {
      const cost = BASE_LOT_BUILD_COST_CR[m.name];
      return cost ? `${cost.toLocaleString()} CR to build` : "";
    }
    if (m.kind === "crewQuarters") {
      const { total } = fleetCrewSummary();
      return `${total} crew across the fleet`;
    }
    if (m.kind === "amnesty") {
      const faction = stationGuardFaction((getSystem(state.currentSystemId)?.region ?? "neutral") as GalaxyRegion);
      if (!faction) return "";
      const owed = amnestyCostCr(state.standing[faction]);
      return owed > 0 ? `${owed.toLocaleString()} CR owed to ${faction}` : `Nothing owed to ${faction}`;
    }
    if (m.kind === "repair") {
      const ship = state.currentShip;
      if (!ship) return "";
      return isDamaged(damageFor(state.shipDamage, state.currentShipInstanceId)) ? "Damage to repair" : "No damage";
    }
    return "";
  }

  /** What the click will actually do, so hovering never guesses. */
  function lotOpenHint(m: LotModule): string {
    if (m.kind === "navigate") return m.navigateLabel ?? "Open";
    if (m.kind === "launch") return variant === "base" ? "Launch into space" : "Undock into space";
    return "Click to open";
  }

  function modalHtml(m: LotModule): string {
    return `
      <div class="lot-modal" data-modal>
        <div class="lot-modal-panel">
          <div class="lot-modal-header">
            <strong>${m.name}</strong>
            <button type="button" class="lot-modal-close" data-modal-close>&times;</button>
          </div>
          <p class="dim">${m.description}</p>
          ${moduleBodyHtml(m)}
        </div>
      </div>
    `;
  }

  /**
   * Provisions consumption, in the module Don named for it (16.103).
   *
   * The important line is the one that corrects a reading the old panel invited:
   * it lists the whole fleet's crew total, but only the FLOWN ship's crew
   * actually eat (`provisions.ts` consumes against `state.currentShip.crew`).
   * A captain comparing "Total crew: 47" against a burn rate would have got the
   * wrong answer, so the burn is labelled with the crew it belongs to and the
   * difference is stated outright.
   */
  /**
   * The pre-undock check (16.104, Don's direction).
   *
   * Three ways out, deliberately, and they map onto the three things a captain
   * might actually want:
   *
   * - **Restock** goes where the supplies are. At a Station that is the Market;
   *   at a Base it is the Cargo transfer with your own stockpile. Don asked for
   *   both by name, and they are different screens, so the button changes.
   * - **Undock anyway** honours the decision. A two-jump errand on fumes is a
   *   legitimate call, and a dialog that refused would be worse than the strand.
   * - **Stay docked** is the cancel, and it is the default focus, because the
   *   dialog only appears when staying is probably right.
   */
  /** The check itself, against the ship actually being flown. */
  function currentUndockWarning(): ReturnType<typeof undockWarning> {
    const ship = state.currentShip;
    if (!ship) {
      // No ship to strand. Every field still has to be real, so the dialog can
      // never be handed a half-built object.
      return undockWarning("Medium", 0, 0, 0, 0, 0);
    }
    return undockWarning(
      ship.hullTierLabel,
      ship.crew,
      state.fuelOnBoard,
      state.fuelCapacity,
      state.provisionsOnBoard,
      state.provisionsCapacity
    );
  }

  function launchNow(): void {
    playLaunchAnimation(variant, () => ctx.navigate("expedition"));
  }
  function undockWarningHtml(w: ReturnType<typeof undockWarning>): string {
    const restockLabel = variant === "base" ? "Open Base Cargo" : "Open Market";
    return `
      <div class="lot-modal" data-modal data-undock-warning>
        <div class="lot-modal-panel">
          <div class="lot-modal-header">
            <strong>${w.strandedNow ? "You cannot get back" : "Short on supplies"}</strong>
            <button type="button" class="lot-modal-close" data-modal-close>&times;</button>
          </div>
          <ul class="undock-reasons">
            ${w.reasons.map((r) => `<li>${r}</li>`).join("")}
          </ul>
          <div class="lot-stat-grid">
            <div${w.lowFuel ? ' class="lot-stat-low"' : ""}>
              <span>Fuel</span>
              <span>${Math.round(w.fuel.onBoard)} / ${Math.round(w.fuel.capacity)} &mdash; ${fuelHeadline(w.fuel)}</span>
            </div>
            <div${w.lowProvisions ? ' class="lot-stat-low"' : ""}>
              <span>Provisions</span>
              <span>${w.provisions.onBoard.toFixed(1)} / ${w.provisions.capacity.toFixed(1)} &mdash; ${formatEndurance(w.provisions.hoursLeft)} left</span>
            </div>
          </div>
          <p class="dim">This is the only place to restock. Both are always in stock at a Station Market.</p>
          <div class="cargo-actions">
            <button type="button" data-undock-restock>${restockLabel}</button>
            <div style="display:flex; gap:0.5rem;">
              <button type="button" class="btn-secondary" data-undock-cancel>Stay docked</button>
              <button type="button" class="btn-danger" data-undock-anyway>Undock anyway</button>
            </div>
          </div>
        </div>
      </div>
    `;
  }

  function crewProvisionsHtml(): string {
    const ship = state.currentShip;
    if (!ship) return "";
    const prov = provisionsSummary(ship.crew, state.provisionsOnBoard, state.provisionsCapacity);
    const { total } = fleetCrewSummary();
    return `
      <div class="lot-stat-grid">
        <div><span>Consuming now</span><span>${prov.perHour.toFixed(1)} / hour</span></div>
        <div><span>Eating crew</span><span>${prov.crew} aboard ${ship.name}</span></div>
        <div><span>Provisions aboard</span><span>${prov.onBoard.toFixed(1)} / ${prov.capacity.toFixed(1)}</span></div>
        <div${prov.low ? ' class="lot-stat-low"' : ""}><span>Runs out in</span><span>${formatEndurance(prov.hoursLeft)}</span></div>
      </div>
      <p class="dim">
        Only the crew of the ship you are flying consume Provisions &mdash; stored and escort
        hulls cost nothing while you are not aboard them.${
          total > prov.crew
            ? ` Of ${total} crew across the fleet, ${prov.crew} are eating.`
            : ""
        }
        Restock at any Station Market, where Provisions are always in stock.
      </p>
    `;
  }

  function moduleBodyHtml(m: LotModule): string {
    if (!builtAt(m)) {
      return buildPromptHtml(m);
    }
    if (m.kind === "navigate") {
      return `<button type="button" data-modal-navigate="${m.navigateTo}">${m.navigateLabel}</button>`;
    }
    if (m.kind === "launch") {
      const label = variant === "base" ? "Launch" : "Undock";
      return `<button type="button" data-modal-launch>${label}</button>`;
    }
    if (m.kind === "crewQuarters") {
      const { total, rows } = fleetCrewSummary();
      if (rows.length === 0) {
        return '<p class="dim">No ships in your fleet yet.</p>';
      }
      return `
        <div class="cargo-list">
          ${rows.map((r) => `<div class="cargo-row"><span>${r.name}</span><span class="dim">${r.crew} crew</span></div>`).join("")}
        </div>
        <p class="dim">Total crew: ${total}. Crew consume Provisions against the real game clock (Section 11/16.62) -- run out and the countdown to mutiny begins.</p>
        ${crewProvisionsHtml()}
      `;
    }
    if (m.kind === "armory") {
      return armoryBodyHtml();
    }
    if (m.kind === "repair") {
      return repairBodyHtml();
    }
    if (m.kind === "amnesty") {
      return amnestyBodyHtml();
    }
    if (m.kind === "planetaryDefenses") {
      return `
        <p class="dim">
          Online. Any hostile coming within ${PLANETARY_DEFENSE_RANGE_M.toLocaleString()}m of this planet
          is acquired and destroyed within a few seconds, automatically -- you don't crew or fire it.
        </p>
        <p class="dim">
          This makes the space around this planet a genuinely safe place to sit, repair, or wait out
          a random attack while in the Expedition View. The Base itself is never targeted.
        </p>
      `;
    }
    // comingSoon
    return `<p class="dim">Coming soon -- see the design doc for why this isn't real yet.</p>`;
  }

  // Lot construction prompt (16.42, Section 12: "tapping... reveals
  // description, cost, and purpose, with a Build/Purchase action") -- the
  // description is already shown above this in modalHtml, so this is just
  // the cost + Build button.
  function buildPromptHtml(m: LotModule): string {
    const cost = BASE_LOT_BUILD_COST_CR[m.name];
    const canAfford = state.credits >= cost;
    return `
      <p class="dim">Not yet built. Cost: ${cost.toLocaleString()} CR.</p>
      <button type="button" data-build-lot ${canAfford ? "" : "disabled"}>
        ${canAfford ? `Build -- ${cost.toLocaleString()} CR` : "Not enough credits"}
      </button>
    `;
  }

  // Every ship the captain owns or has hired that's carrying real damage
  // (16.81), flown hull first. Deliberately not filtered by where each
  // ship is parked: Crew Quarters already summarizes the whole fleet the
  // same way, and a captain who has to fly each escort to a yard
  // individually would just never repair them.
  function damagedShips(): { instanceId: string; ship: PlayerShip; label: string; damage: ShipDamage }[] {
    const rows: { instanceId: string; ship: PlayerShip; label: string; damage: ShipDamage }[] = [];
    if (state.currentShip && state.currentShipInstanceId) {
      rows.push({
        instanceId: state.currentShipInstanceId,
        ship: state.currentShip,
        label: `${state.currentShip.name} (flying)`,
        damage: damageFor(state.shipDamage, state.currentShipInstanceId)
      });
    }
    state.fleet.forEach((f) => {
      if (f.id === state.currentShipInstanceId) return;
      rows.push({
        instanceId: f.id,
        ship: f.ship,
        label: `${f.ship.name} (${f.source})`,
        damage: damageFor(state.shipDamage, f.id)
      });
    });
    return rows.filter((r) => isDamaged(r.damage));
  }

  // Charges both currencies, then clears that ship's damage record. A
  // partial repair isn't offered: the interesting decision is which venue
  // to use and whether you can afford it, not how many HP to buy.
  function repairShip(instanceId: string): void {
    const row = damagedShips().find((r) => r.instanceId === instanceId);
    if (!row) return;
    const quote = repairQuote(row.ship, row.damage, variant);
    const ferriteAboard = state.cargo[REPAIR_ORE] ?? 0;
    if (!canAffordRepair(quote, state.credits, ferriteAboard)) return;
    state.credits -= quote.credits;
    const left = ferriteAboard - quote.ferrite;
    if (left > 0.0001) state.cargo[REPAIR_ORE] = left;
    else delete state.cargo[REPAIR_ORE];
    delete state.shipDamage[instanceId];
    state.lastEvent = `Repaired ${row.ship.name} — ${repairCostLabel(quote)}.`;
    triggerSave(ctx);
  }

  /**
   * The Registry Office (16.95): pay off a faction grievance.
   *
   * Priced at 150,000 CR per point of negative standing, so clearing full
   * hostility costs 1,500,000 CR -- four times the priciest hull in the game,
   * which is what Don asked for. Linear rather than curved: the debt already
   * scales with how badly the captain behaved, and a curve on top would make
   * deep hostility effectively permanent, which is the exact outcome an
   * amnesty exists to prevent.
   *
   * Only ever offers the faction that owns THIS station. Buying forgiveness
   * from the Crimson Concord at an Azure desk would make the whole alignment
   * system a formality.
   */
  function amnestyBodyHtml(): string {
    const system = getSystem(state.currentSystemId);
    const faction = stationGuardFaction((system?.region ?? "neutral") as GalaxyRegion);
    if (!faction) {
      return `<p class="dim">This station answers to no faction, so there is nothing here to forgive.</p>`;
    }
    const value = state.standing[faction];
    if (value >= 0) {
      return `<p class="dim">Your record with the <strong>${faction}</strong> is clean (${value.toFixed(2)}). Nothing to settle.</p>`;
    }
    const cost = amnestyCostCr(value);
    const affordable = state.credits >= cost;
    return `
      <p class="dim">
        Your standing with the <strong>${faction}</strong> is <strong>${value.toFixed(2)}</strong>
        (${standingTier(value)}). Settling clears it to zero.
      </p>
      <p class="dim">
        Fee: <strong>${cost.toLocaleString()} CR</strong> &middot; you hold ${state.credits.toLocaleString()} CR.
        Nothing about standing decays on its own &mdash; this is the only way back.
      </p>
      <button type="button" data-amnesty ${affordable ? "" : "disabled"}>
        ${affordable ? `Pay ${cost.toLocaleString()} CR` : "Insufficient credits"}
      </button>
    `;
  }
  function repairBodyHtml(): string {
    const rows = damagedShips();
    const ferriteAboard = state.cargo[REPAIR_ORE] ?? 0;
    if (rows.length === 0) {
      return '<p class="dim">Every ship in your fleet is at full hull and shields. Nothing to repair.</p>';
    }
    return `
      <div class="cargo-list">
        ${rows
          .map((r) => {
            const quote = repairQuote(r.ship, r.damage, variant);
            const affordable = canAffordRepair(quote, state.credits, ferriteAboard);
            const hullPct = Math.round(((r.ship.hullHp - quote.hullHp) / r.ship.hullHp) * 100);
            const shieldPct =
              r.ship.shieldHp > 0
                ? Math.round(((r.ship.shieldHp - quote.shieldHp) / r.ship.shieldHp) * 100)
                : null;
            return `
              <div class="cargo-row">
                <span>
                  ${r.label} — hull ${hullPct}%${shieldPct !== null ? `, shields ${shieldPct}%` : ""}
                </span>
                <button type="button" data-repair-ship="${r.instanceId}" ${affordable ? "" : "disabled"}>
                  ${affordable ? `Repair — ${repairCostLabel(quote)}` : `Need ${repairCostLabel(quote)}`}
                </button>
              </div>
            `;
          })
          .join("")}
      </div>
      <p class="dim">
        ${
          variant === "base"
            ? "No service fee here -- your yard, your crew. Materials only."
            : `A faction yard charges ${Math.round(STATION_REPAIR_FEE_FRACTION * 100)}% of the hull's own price for a full repair, plus materials.`
        }
        Hull plating costs ${HP_PER_FERRITE} HP per ${REPAIR_ORE} (16.80); shields are powered, not built, and cost no materials.
        You have ${state.credits.toLocaleString()} CR and ${ferriteAboard.toFixed(1)} ${REPAIR_ORE} aboard.
      </p>
    `;
  }

  function armoryBodyHtml(): string {
    const ship = state.currentShip;
    if (!ship || !state.currentShipInstanceId) {
      return '<p class="dim">No ship selected.</p>';
    }
    if (ship.dps <= 0) {
      return `<p class="dim">${ship.name} has no combat Hardpoints to upgrade (mining/hauling hulls carry none).</p>`;
    }
    const instanceId = state.currentShipInstanceId;
    return `
      <div class="cargo-list">
        ${WEAPON_KEYS.map((key) => {
          const level = weaponLevel(state.weaponUpgrades, instanceId, key);
          const maxed = level >= MAX_WEAPON_LEVEL;
          const cost = maxed ? null : WEAPON_UPGRADE_COST_CR[level + 1];
          return `
            <div class="cargo-row">
              <span>${WEAPON_LABELS[key]} -- Level ${level}${maxed ? " (max)" : ""}</span>
              <button type="button" data-upgrade-weapon="${key}" ${maxed || (cost !== null && state.credits < cost) ? "disabled" : ""}>
                ${maxed ? "Maxed" : `Upgrade -- ${cost!.toLocaleString()} CR`}
              </button>
            </div>
          `;
        }).join("")}
      </div>
      <p class="dim">Levels reset to 1 if ${ship.name} is destroyed.</p>
    `;
  }

  render();
}

// A brief full-screen flash, not a real flight sequence -- Launchpad/Dock
// only need to feel like a distinct beat before the Expedition View loads,
// per Don's "launch animation"/"undocking animation" direction. Reuses
// the Expedition View's own arrival-beat visual language (a fading
// full-screen overlay) rather than inventing a second, inconsistent style.
function playLaunchAnimation(variant: LotVariant, onDone: () => void): void {
  // Counts as a transition for message purposes (16.98) -- this overlay is what
  // the undock banner used to fire underneath.
  beginScreenTransition();
  const overlay = document.createElement("div");
  overlay.className = "lot-launch-overlay";
  overlay.innerHTML = `<div>${variant === "base" ? "LAUNCHING —" : "UNDOCKING —"}</div>`;
  document.body.appendChild(overlay);
  requestAnimationFrame(() => overlay.classList.add("show"));
  setTimeout(() => {
    onDone();
    setTimeout(() => {
      overlay.remove();
      // Ends AFTER onDone, so the Expedition View's own arrival beat has already
      // opened its own transition and the gate never briefly falls to zero
      // between the two overlays.
      endScreenTransition();
    }, 50);
  }, 1200);
}
